/**
 * CallHistoryPanel — slide-out drawer showing recent calls.
 * Fetches from GET /api/voice/calls via client/lib/voice-api.ts.
 * Click to dial back via window.ApexSoftphone.dial().
 */

import { useState, useEffect, useCallback } from 'react';
import {
  X,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Clock,
  ChevronDown,
  Disc,
  Users,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useMyNumbers } from '../../hooks/useMyNumbers';

interface CallEntry {
  id: string;
  direction: 'inbound' | 'outbound';
  from: string;
  to: string;
  contact_name?: string;
  contact_id?: string;
  status: string;
  duration_secs?: number;
  started_at: string;
  answered_at?: string;
  ended_at?: string;
  recording_count: number;
  participant_count: number;
}

interface CallHistoryResponse {
  calls: CallEntry[];
  total: number;
  page: number;
  per_page: number;
}

function formatDuration(secs?: number): string {
  if (!secs) return '0:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function relativeTime(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'Yesterday';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function DirectionIcon({ direction, status }: { direction: string; status: string }) {
  const missed = status === 'missed' || status === 'no-answer';
  if (missed) return <PhoneMissed className="w-4 h-4 text-destructive stroke-[1.5]" />;
  if (direction === 'inbound')
    return <PhoneIncoming className="w-4 h-4 text-brand-green stroke-[1.5]" />;
  return <PhoneOutgoing className="w-4 h-4 text-primary stroke-[1.5]" />;
}

function statusColor(status: string): string {
  switch (status) {
    case 'completed':
      return 'text-brand-green';
    case 'missed':
    case 'no-answer':
      return 'text-destructive';
    case 'busy':
    case 'failed':
    case 'cancelled':
      return 'text-muted-foreground';
    default:
      return 'text-foreground';
  }
}

export function CallHistoryPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { fromNumber, canVoice } = useMyNumbers();
  const [calls, setCalls] = useState<CallEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<'all' | 'inbound' | 'outbound' | 'missed'>('all');

  const loadCalls = useCallback(async (pageNum: number) => {
    setLoading(true);
    try {
      // Dynamic import to avoid pulling voice-api into the main bundle
      const { fetchCallHistory } = await import('../../../lib/voice-api');
      const data: CallHistoryResponse = await fetchCallHistory({
        page: pageNum,
        per_page: 25,
      });
      if (pageNum === 1) {
        setCalls(data.calls);
      } else {
        setCalls((prev) => [...prev, ...data.calls]);
      }
      setTotal(data.total);
    } catch {
      // Voice service may not be available
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      setPage(1);
      loadCalls(1);
    }
  }, [open, loadCalls]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadCalls(nextPage);
  };

  const filtered = calls.filter((c) => {
    if (filter === 'all') return true;
    if (filter === 'missed') return c.status === 'missed' || c.status === 'no-answer';
    return c.direction === filter;
  });

  const handleDial = (call: CallEntry) => {
    if (!canVoice) return;
    const number = call.direction === 'inbound' ? call.from : call.to;
    const sp = (window as unknown as Record<string, unknown>)['ApexSoftphone'] as
      | {
          dial: (id: string | null, name: string | null, phone: string, fromNumber: string) => void;
        }
      | undefined;
    sp?.dial(call.contact_id || null, call.contact_name || null, number, fromNumber);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 h-full w-[380px] bg-card border-l border-border/60 z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary stroke-[1.5]" />
                <h3 className="text-[0.9375rem] font-semibold text-foreground">Call History</h3>
                <span className="text-[0.6875rem] text-muted-foreground">({total})</span>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors cursor-pointer"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {/* Filter tabs */}
            <div className="px-5 py-2 border-b border-border/60 flex gap-2">
              {(['all', 'inbound', 'outbound', 'missed'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 rounded-lg text-[0.6875rem] font-semibold transition-colors cursor-pointer capitalize ${
                    filter === f
                      ? 'bg-primary/10 text-primary border border-primary/20'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Call list */}
            <div className="flex-1 overflow-y-auto">
              {loading && calls.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
                  <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin mr-2" />
                  Loading...
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <Phone className="w-8 h-8 mb-2 opacity-30" />
                  <span className="text-[0.8125rem]">No calls found</span>
                </div>
              ) : (
                <>
                  {filtered.map((call) => {
                    const displayName =
                      call.contact_name || (call.direction === 'inbound' ? call.from : call.to);
                    const missed = call.status === 'missed' || call.status === 'no-answer';

                    return (
                      <button
                        key={call.id}
                        onClick={() => handleDial(call)}
                        disabled={!canVoice}
                        title={
                          canVoice
                            ? undefined
                            : 'Selected number does not support voice — pick a call-capable number'
                        }
                        className="w-full px-5 py-3 flex items-center gap-3 hover:bg-secondary/50 transition-colors cursor-pointer border-b border-border/40 text-left disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <DirectionIcon direction={call.direction} status={call.status} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span
                              className={`text-[0.8125rem] font-medium truncate ${missed ? 'text-destructive' : 'text-foreground'}`}
                            >
                              {displayName}
                            </span>
                            <span className="text-[0.625rem] text-muted-foreground flex-shrink-0 ml-2">
                              {relativeTime(call.started_at)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span
                              className={`text-[0.625rem] font-bold uppercase ${statusColor(call.status)}`}
                            >
                              {call.status.replace(/-/g, ' ')}
                            </span>
                            {call.duration_secs != null && call.duration_secs > 0 && (
                              <span className="text-[0.625rem] text-muted-foreground">
                                {formatDuration(call.duration_secs)}
                              </span>
                            )}
                            {/* Recording / participant counts (#553) */}
                            {call.recording_count > 0 && (
                              <span
                                className="flex items-center gap-0.5 text-[0.625rem] text-muted-foreground"
                                title={`${call.recording_count} recording${call.recording_count === 1 ? '' : 's'}`}
                              >
                                <Disc className="w-3 h-3 stroke-[1.5]" />
                                {call.recording_count}
                              </span>
                            )}
                            {call.participant_count > 0 && (
                              <span
                                className="flex items-center gap-0.5 text-[0.625rem] text-muted-foreground"
                                title={`${call.participant_count} participant${call.participant_count === 1 ? '' : 's'}`}
                              >
                                <Users className="w-3 h-3 stroke-[1.5]" />
                                {call.participant_count}
                              </span>
                            )}
                          </div>
                        </div>
                        <Phone className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" />
                      </button>
                    );
                  })}

                  {/* Load more */}
                  {calls.length < total && (
                    <button
                      onClick={loadMore}
                      disabled={loading}
                      className="w-full py-3 text-[0.75rem] font-semibold text-primary hover:bg-primary/5 transition-colors cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                      {loading ? 'Loading...' : 'Load more'}
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
